import React, { useState } from 'react';
import { t } from '../common/LocalizationProvider';
import { prefixString } from '../common/stringUtils';
import EditItemView from '../shared/EditItemView';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  makeStyles,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  InputAdornment
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import EditAttributesView from '../attributes/EditAttributesView';
import positionAttributes from '../attributes/positionAttributes';
import { useAttributePreference } from '../common/preferences';
import { speedFromKnots, speedToKnots, distanceFromMeters, distanceToMeters } from '../common/converter';

import { cyberStyles } from '../cssShared/cyberTheme';

const useStyles = makeStyles((theme) => ({
  ...cyberStyles(theme),
  selectContainer: {
    '& .MuiInputLabel-root': {
      color: '#ffffff', // Label white
      opacity: 0.8,
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      border: '1px solid rgba(0, 242, 255, 0.3)',
      borderRadius: '8px',
      color: '#ffffff', // Selected text white
      '&:before, &:after': { display: 'none' },
    },
    '& .MuiSelect-icon': {
      color: '#00f2ff', // Keeping icon cyan for "Cyber" feel, change to #fff if preferred
    },
  },
  menuItem: {
    backgroundColor: '#090d11',
    color: '#ffffff', // Dropdown options white
    '&:hover': {
      backgroundColor: 'rgba(0, 242, 255, 0.1)',
    },
  },
  unitLabel: {
    color: '#ffffff !important', // Unit text (km, knots) white
    opacity: 0.9,
  }
}));

const MaintenancePage = () => {
  const classes = useStyles();
  const [item, setItem] = useState();
  const [labels, setLabels] = useState({start: '', period: ''});

  const speedUnit = useAttributePreference('speedUnit');
  const distanceUnit = useAttributePreference('distanceUnit', 'km');

  // Logic remains identical to preserve functionality
  const convertToList = (attributes) => {
    let otherList = [];
    for (const key in attributes) {
      const value = attributes[key];
      if (value.type === 'number') {
        otherList.push({key, name: value.name, type: value.type});
      }
    }
    return otherList;
  }

  const onMaintenanceTypeChange = event => {
    const newValue = event.target.value;
    setItem({ ...item, type: newValue, start: 0, period: 0 });
    const attribute = positionAttributes[newValue];
    if (attribute && attribute.dataType) {
      switch (attribute.dataType) {
        case 'distance':
          setLabels({ ...labels, start: t(prefixString('shared', distanceUnit)), period: t(prefixString('shared', distanceUnit))});
          break;
        case 'speed':
          setLabels({ ...labels, start: t(prefixString('shared', speedUnit)), period: t(prefixString('shared', speedUnit))});
          break;
        default:
          setLabels({ ...labels, start: null, period: null });
          break;
      }
    }
  }

  const rawToValue = value => {
    const attribute = positionAttributes[item?.type];
    if (attribute && attribute.dataType) {
      switch (attribute.dataType) {
        case 'speed': return speedFromKnots(value, speedUnit);
        case 'distance': return distanceFromMeters(value, distanceUnit);
        default: return value;
      }
    }
    return value;
  }

  const valueToRaw = value => {
    const attribute = positionAttributes[item?.type];
    if (attribute && attribute.dataType) {
      switch (attribute.dataType) {
        case 'speed': return speedToKnots(value, speedUnit);
        case 'distance': return distanceToMeters(value, distanceUnit);
        default: return value;
      }
    }
    return value;
  }

  return (
      <EditItemView endpoint="maintenance" item={item} setItem={setItem}>
        {item &&
            <>
              <Accordion defaultExpanded className={classes.accordion}>
                <AccordionSummary expandIcon={<ExpandMoreIcon style={{color: '#00f2ff'}} />}>
                  <Typography className={classes.summaryText}>
                    {t('sharedRequired')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.details}>

                  <Box className={classes.fieldWrapper}>
                    <TextField
                        className={classes.neonInput}
                        fullWidth
                        value={item.name || ''}
                        onChange={event => setItem({...item, name: event.target.value})}
                        label={t('sharedName')}
                        variant="filled" />
                  </Box>

                  <Box className={classes.fieldWrapper}>
                    <FormControl variant="filled" fullWidth className={classes.selectContainer}>
                      <InputLabel>{t('sharedType')}</InputLabel>
                      <Select
                          value={item.type || ''}
                          onChange={onMaintenanceTypeChange}
                          MenuProps={{PaperProps: { className: classes.periodMenuPaper },}}
                      >
                        {convertToList(positionAttributes).map(({ key, name })=>(
                            <MenuItem key={key} value={key} className={classes.menuItem}>{name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box className={classes.fieldWrapper}>
                    <TextField
                        className={classes.neonInput}
                        fullWidth
                        type="number"
                        value={rawToValue(item.start) || ''}
                        onChange={event => setItem({...item, start: valueToRaw(event.target.value)})}
                        label={t('start')}
                        variant="filled"
                        InputProps={{
                          endAdornment: <InputAdornment position="end" className={classes.unitLabel}>{labels.start}</InputAdornment>,
                        }} />
                  </Box>

                  <Box>
                    <TextField
                        className={classes.neonInput}
                        fullWidth
                        type="number"
                        value={rawToValue(item.period) || ''}
                        onChange={event => setItem({...item, period: valueToRaw(event.target.value)})}
                        label={t('maintenancePeriod')}
                        variant="filled"
                        InputProps={{
                          endAdornment: <InputAdornment position="end" className={classes.unitLabel}>{labels.period}</InputAdornment>,
                        }} />
                  </Box>

                </AccordionDetails>
              </Accordion>

              <Accordion className={classes.accordion}>
                <AccordionSummary expandIcon={<ExpandMoreIcon style={{color: '#00f2ff'}} />}>
                  <Typography className={classes.summaryText}>
                    {t('sharedAttributes')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <EditAttributesView
                      attributes={item.attributes}
                      setAttributes={attributes => setItem({...item, attributes})}
                      definitions={{}}
                  />
                </AccordionDetails>
              </Accordion>
            </>
        }
      </EditItemView>
  );
}

export default MaintenancePage;